import type { ISODate } from './types';

/**
 * Aritmética de FECHAS DE CALENDARIO ('YYYY-MM-DD') sin zona horaria.
 * Regla de la arquitectura: nunca `new Date('2026-10-12')` (se interpreta en UTC y en UTC-4 "corre" un día).
 * Todo el cálculo pasa por Date.UTC, así que no depende de la zona del navegador ni del servidor de pruebas.
 */
export interface CalendarDate {
  y: number;
  /** 1–12 */
  m: number;
  /** 1–31 */
  d: number;
}

const ISO_PREFIX = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s])/;
const MS_PER_DAY = 86_400_000;

const pad = (n: number, width: number): string => String(n).padStart(width, '0');

/** Lee 'YYYY-MM-DD' (o 'YYYY-MM-DDThh:mm…', ignorando la hora). Devuelve null si no es una fecha real. */
export function parseISODate(value: unknown): CalendarDate | null {
  if (typeof value !== 'string') return null;
  const match = ISO_PREFIX.exec(value.trim());
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  const probe = new Date(Date.UTC(y, m - 1, d));
  const real =
    probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
  return real ? { y, m, d } : null;
}

export const isISODate = (value: unknown): value is ISODate => parseISODate(value) !== null;

export const toISODate = ({ y, m, d }: CalendarDate): ISODate =>
  `${pad(y, 4)}-${pad(m, 2)}-${pad(d, 2)}`;

/** Número de día absoluto (días desde 1970-01-01) de una fecha de calendario. */
export const dayNumber = ({ y, m, d }: CalendarDate): number =>
  Math.round(Date.UTC(y, m - 1, d) / MS_PER_DAY);

const fromDayNumber = (n: number): CalendarDate => {
  const date = new Date(n * MS_PER_DAY);
  return { y: date.getUTCFullYear(), m: date.getUTCMonth() + 1, d: date.getUTCDate() };
};

/** Suma (o resta) días a una fecha. Si `iso` no es válida devuelve null. */
export function addDays(iso: ISODate, days: number): ISODate | null {
  const parts = parseISODate(iso);
  return parts ? toISODate(fromDayNumber(dayNumber(parts) + Math.trunc(days))) : null;
}

/** Día de la semana: 0 = domingo … 6 = sábado. */
export function weekday(iso: ISODate): number | null {
  const parts = parseISODate(iso);
  return parts ? new Date(Date.UTC(parts.y, parts.m - 1, parts.d)).getUTCDay() : null;
}

/** Días de calendario entre `a` y `b` (b − a). NaN si alguna no es una fecha válida. */
export function diasEntre(a: ISODate, b: ISODate): number {
  const pa = parseISODate(a);
  const pb = parseISODate(b);
  return pa && pb ? dayNumber(pb) - dayNumber(pa) : Number.NaN;
}

/** Normaliza cualquier texto con fecha (con hora o no) a 'YYYY-MM-DD'. null si no es una fecha real. */
export function soloFecha(value: unknown): ISODate | null {
  const parts = parseISODate(value);
  return parts ? toISODate(parts) : null;
}

/** Comparador de fechas ISO (orden lexicográfico = cronológico). */
export const compareISO = (a: ISODate, b: ISODate): number => (a < b ? -1 : a > b ? 1 : 0);

/** Primer día del mes (YYYY-MM-01) y lunes de la semana de una fecha: claves de agrupación. */
export function inicioDeMes(iso: ISODate): ISODate | null {
  const parts = parseISODate(iso);
  return parts ? toISODate({ ...parts, d: 1 }) : null;
}

export function inicioDeSemana(iso: ISODate): ISODate | null {
  const wd = weekday(iso);
  if (wd === null) return null;
  return addDays(iso, -((wd + 6) % 7));
}
